import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Heart, Sparkles, Brain, Users, Activity, BookOpen,
  Compass, BarChart3, Lock, GraduationCap, LifeBuoy, Target,
  ShieldCheck, Microscope, HeartHandshake,
} from 'lucide-react';

import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import HeroSection from '../components/landing/HeroSection';
import DownloadSection from '../components/landing/DownloadSection';
import { listPrograms, upcomingPrograms } from '../data/programs';
import { safeJsonLd } from '../utils/seo';

import '../styles/pages/AboutPage.css';

// Three foundational pillars — they drive every product decision at LifeUp.
const PILLARS = [
  {
    icon: <ShieldCheck size={22} />,
    title: 'Privacidade radical',
    description:
      'Sem identidade exposta, sem perfis públicos. Diário criptografado e comunidade anônima desde o primeiro toque.',
  },
  {
    icon: <Microscope size={22} />,
    title: 'Baseado em ciência',
    description:
      'Conteúdo validado por psicólogos e referências em neurociência da recuperação. Sem promessas mágicas.',
  },
  {
    icon: <HeartHandshake size={22} />,
    title: 'Comunidade humana',
    description:
      'Pessoas reais em jornadas reais. Apoio entre pares, mediado por voluntários treinados, disponível 24/7.',
  },
];

// What the apps actually deliver — common stack across every program.
const PLATFORM_PILLARS = [
  {
    icon: <Compass size={22} />,
    title: 'Jornadas guiadas',
    description: 'Plano diário sob medida do dia 1 ao dia 365, por programa.',
  },
  {
    icon: <Activity size={22} />,
    title: 'Check-ins diários',
    description: 'Diário emocional, gatilhos e humor mapeados todos os dias.',
  },
  {
    icon: <Sparkles size={22} />,
    title: 'IA Companion',
    description: 'Apoio conversacional 24/7 com privacidade total.',
  },
  {
    icon: <Users size={22} />,
    title: 'Comunidade anônima',
    description: 'Grupos blindados de pessoas em jornadas iguais à sua.',
  },
  {
    icon: <LifeBuoy size={22} />,
    title: 'Protocolo de emergência',
    description: 'Técnicas anti-craving acionáveis em segundos.',
  },
  {
    icon: <BarChart3 size={22} />,
    title: 'Relatórios de progresso',
    description: 'Métricas semanais que tornam cada pequeno avanço visível.',
  },
  {
    icon: <BookOpen size={22} />,
    title: 'Conteúdo educacional',
    description: 'Artigos, vídeos e podcasts baseados em neurociência.',
  },
  {
    icon: <Lock size={22} />,
    title: 'Diário criptografado',
    description: 'Tudo que você escreve fica blindado ponta-a-ponta.',
  },
];

// How the team works — three real roles, no fake bios.
const TEAM = [
  {
    icon: <Brain size={22} />,
    title: 'Produto e engenharia',
    description:
      'Time enxuto e remoto que constrói os apps com foco em performance, privacidade e acessibilidade.',
  },
  {
    icon: <GraduationCap size={22} />,
    title: 'Conselho científico',
    description:
      'Psicólogos e especialistas em comportamento revisam roteiros, conteúdos e protocolos antes de irem ao ar.',
  },
  {
    icon: <Heart size={22} />,
    title: 'Mediadores da comunidade',
    description:
      'Voluntários treinados acompanham os grupos e ativam protocolos de emergência quando necessário.',
  },
];

const AboutPage = () => {
  const navigate = useNavigate();
  const live = listPrograms();

  const goToDownload = () => navigate('/assinar');
  const goToPrograms = () => document.getElementById('about-programs')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <>
      <Helmet>
        <title>Sobre a LifeUp — Plataforma de bem-estar comportamental</title>
        <meta
          name="description"
          content="A LifeUp é uma plataforma multi-programa de bem-estar comportamental. Conheça nossa missão, pilares e como construímos os programas Freedom, Balance e os próximos."
        />
        <link rel="canonical" href="https://lifyup.com.br/sobre" />
        <meta property="og:title" content="Sobre a LifeUp" />
        <meta property="og:description" content="Plataforma multi-programa de bem-estar comportamental e suporte de comunidade." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lifyup.com.br/sobre" />
        <script type="application/ld+json">{safeJsonLd({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'Sobre a LifeUp',
          url: 'https://lifyup.com.br/sobre',
          mainEntity: {
            '@type': 'Organization',
            name: 'LifeUp',
            url: 'https://lifyup.com.br',
            description: 'Plataforma multi-programa de bem-estar comportamental.',
          },
        })}</script>
      </Helmet>

      <div className="lp-theme about-page" style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
        <Header />
        <main>
          <HeroSection
            eyebrow="Sobre a LifeUp"
            title="Tecnologia, ciência e comunidade contra os ciclos que travam vidas"
            subtitle="A LifeUp é uma plataforma multi-programa de bem-estar comportamental. Cada programa é estruturado, anônimo e construído com apoio de profissionais da saúde mental."
            badges={[
              { icon: <Shield size={16} />,   label: 'Privacidade absoluta' },
              { icon: <Heart size={16} />,    label: 'Comunidade 24/7' },
              { icon: <Sparkles size={16} />, label: 'Baseado em ciência' },
            ]}
            primaryCta={{ label: 'Conhecer os programas', onClick: goToPrograms }}
            secondaryCta={{ label: 'Baixar o app', onClick: goToDownload }}
            image="/assets/images/app-mockup.svg"
          />

          {/* ===== Missão ===== */}
          <section className="about-mission">
            <div className="container">
              <div className="about-mission__grid">
                <motion.div
                  className="about-mission__text"
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <span className="about-eyebrow">Nossa missão</span>
                  <h2 className="about-mission__title">
                    Quebrar ciclos comportamentais com estrutura, dados e apoio humano
                  </h2>
                  <p className="about-mission__lead">
                    Comportamentos compulsivos diferentes — pornografia, apostas, telas, álcool —
                    compartilham padrões neuroquímicos parecidos. Nossa abordagem é{' '}
                    <strong>estrutural</strong>: o mesmo motor de plano diário, comunidade e
                    métricas roda em todos os programas, adaptado ao contexto de cada um.
                  </p>
                  <p className="about-mission__lead">
                    Acreditamos que recuperação não é solitária e não é sobre força de vontade.
                    É sobre <strong>ambiente</strong>: ferramentas certas, no momento certo,
                    com pessoas que entendem.
                  </p>
                </motion.div>

                <motion.div
                  className="about-mission__pillars"
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  {PILLARS.map((p, i) => (
                    <article key={i} className="about-pillar">
                      <div className="about-pillar__icon">{p.icon}</div>
                      <div>
                        <h3>{p.title}</h3>
                        <p>{p.description}</p>
                      </div>
                    </article>
                  ))}
                </motion.div>
              </div>
            </div>
          </section>

          {/* ===== O que oferecemos (plataforma) ===== */}
          <section className="about-platform" aria-labelledby="about-platform-title">
            <div className="container">
              <header className="about-header">
                <span className="about-eyebrow">O que oferecemos</span>
                <h2 id="about-platform-title" className="about-header__title">
                  Uma plataforma única, vários programas
                </h2>
                <p className="about-header__subtitle">
                  Recursos comuns a todos os programas LifeUp, disponíveis no app desde o
                  primeiro dia — gratuitos ou no plano Premium.
                </p>
              </header>

              <div className="about-platform__grid">
                {PLATFORM_PILLARS.map((item, i) => (
                  <motion.article
                    key={i}
                    className="about-platform__card"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                  >
                    <div className="about-platform__icon">{item.icon}</div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>

          {/* ===== Programas atuais ===== */}
          <section className="about-programs" id="about-programs" aria-labelledby="about-programs-title">
            <div className="container">
              <header className="about-header">
                <span className="about-eyebrow">Programas LifeUp</span>
                <h2 id="about-programs-title" className="about-header__title">
                  Disponíveis hoje, mais a caminho
                </h2>
                <p className="about-header__subtitle">
                  Cada programa é desenhado para um tema específico, com plano diário,
                  comunidade e ferramentas dedicadas — sob a mesma plataforma.
                </p>
              </header>

              <div className="about-programs__grid">
                {live.map((p) => (
                  <article
                    key={p.slug}
                    className="about-program-card"
                    style={{ '--card-primary': p.colors.primary, '--card-glow': p.colors.glow }}
                  >
                    <span className="about-program-card__status about-program-card__status--live">
                      Disponível
                    </span>
                    <h3>{p.name}</h3>
                    <p className="about-program-card__tag">{p.tagline}</p>
                    <p className="about-program-card__desc">{p.description}</p>
                    <a className="about-program-card__link" href={`/${p.slug}`}>
                      Saiba mais →
                    </a>
                  </article>
                ))}

                {upcomingPrograms.map((p) => (
                  <article
                    key={p.slug}
                    className="about-program-card about-program-card--soon"
                    style={{ '--card-primary': p.colors.primary, '--card-glow': p.colors.glow }}
                  >
                    <span className="about-program-card__status about-program-card__status--soon">
                      Em breve
                    </span>
                    <h3>{p.name}</h3>
                    <p className="about-program-card__tag">{p.tagline}</p>
                    <p className="about-program-card__desc">{p.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* ===== Equipe ===== */}
          <section className="about-team" aria-labelledby="about-team-title">
            <div className="container">
              <header className="about-header">
                <span className="about-eyebrow">Como trabalhamos</span>
                <h2 id="about-team-title" className="about-header__title">
                  Um time enxuto, com apoio de quem entende do assunto
                </h2>
                <p className="about-header__subtitle">
                  Não somos uma clínica nem um substituto para tratamento profissional. Somos
                  uma plataforma de apoio que se apoia em quem é referência clínica.
                </p>
              </header>

              <div className="about-team__grid">
                {TEAM.map((m, i) => (
                  <motion.article
                    key={i}
                    className="about-team__card"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.45, delay: i * 0.08 }}
                  >
                    <div className="about-team__icon">{m.icon}</div>
                    <h3>{m.title}</h3>
                    <p>{m.description}</p>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>

          <DownloadSection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default AboutPage;
